import React from "react";

import { FolderPrimaryListDto } from "@common/dto/folder/primary-list-dto";
import { trDts } from "@modules/datasheets/utils/i18n";
import { DatasheetFormatType } from "@common/dto/datasheet/type";
import { useMessage } from "@common/hooks/message";
import { HttpErrorDto } from "@common/dto/http";
import {
  createDatasheetFolder,
  deleteDatasheetFolder,
  getDatasheetFolders,
} from "@modules/datasheets/services/folders";
import { createDatasheetFormat } from "@modules/datasheets/services/datasheet";
import { deleteDatasheetFormat, renameDatasheetFormat } from "@modules/datasheets/services/formats";

import {
  DatasheetFormatManagementConfig,
  DatasheetFormatManagementFormat,
  DatasheetFormatManagementMode,
  DatasheetFormatManagementProps,
  DatasheetFormatManagementSubmitExportParams,
} from "./datasheet-format-management.types";
import { getDatasheetResultFolderFormats, getTdsResultFolderFormats } from "@modules/datasheets/services/formats";
import { executeUI } from "@common/utils/ui-bridge";
import { QuestionMessage } from "@common/components/question-message";

export const useController = (props: DatasheetFormatManagementProps) => {
  const {
    onSubmit,
    /*getFormats, /* getFolders, */ mode,
    
    importContext,
    importFormatName,
    importTableFieldId,
    onClose,
    initialSelectedFormatType,
    initialTableField,
  } = props;

  const { closeMessage, showConfirmMessage, messageProps, showErrorMessage } = useMessage();

  const [selectedFormatType, setSelectedFormatType] = React.useState<DatasheetFormatType>(
    initialSelectedFormatType ?? DatasheetFormatType.individual
  );
  const [formatName, setFormatName] = React.useState<string>("");
  const [folders, setFolders] = React.useState<Array<FolderPrimaryListDto>>([]);
  const [formats, setFormats] = React.useState<Array<DatasheetFormatManagementFormat>>([]);
  const [selectedFolder, setSelectedFolder] = React.useState<FolderPrimaryListDto | undefined>();
  const [selectedFormat, setSelectedFormat] = React.useState<{ id: number; name: string } | undefined>();

  // For TDS (table) datasheets – which table field this format is bound to
  const [selectedTableFieldId, setSelectedTableFieldId] = React.useState<number | null>(
    initialTableField?.id ?? null
  );
  const [selectedTableFieldName, setSelectedTableFieldName] = React.useState<string>(
    initialTableField?.name ?? ""
  );
  const [selectedTableFieldContext, setSelectedTableFieldContext] = React.useState<number>(
    initialTableField?.context ?? 1
  );
  const [folderPickerOpened, setFolderPickerOpened] = React.useState(false);
  const [renameState, setRenameState] = React.useState<{
    folderId: number | null;
    formatId: number | null;
    name: string;
    opened: boolean;
  }>({
    folderId: null,
    formatId: null,
    name: "",
    opened: false,
  });
  const [successToastMessage, setSuccessToastMessage] = React.useState<string | null>(null);
  const clearSuccessToastMessage = () => setSuccessToastMessage(null);
  const renameOverwritePromptVisibleRef = React.useRef(false);

  React.useEffect(() => {
    if (!successToastMessage) {
      return;
    }
    const timer = window.setTimeout(() => setSuccessToastMessage(null), 3000);
    return () => window.clearTimeout(timer);
  }, [successToastMessage]);

  React.useEffect(() => {
    if (!messageProps?.show) {
      renameOverwritePromptVisibleRef.current = false;
    }
  }, [messageProps?.show]);

  // When we are in import mode, derive the datasheet type from the uploaded XML context.
  // This ensures the folder picker loads folders for the correct context (IDS / PDS / TDS).
  React.useEffect(() => {
    if (mode !== DatasheetFormatManagementMode.import || importContext == null) {
      return;
    }

    if (importContext === 2) {
      setSelectedFormatType(DatasheetFormatType.pedigree);
    } else if (importContext === 8) {
      setSelectedFormatType(DatasheetFormatType.table);
    } else {
      setSelectedFormatType(DatasheetFormatType.individual);
    }
  }, [mode, importContext]);

  // Auto-open folder picker for import mode
  React.useEffect(() => {
    if (mode === DatasheetFormatManagementMode.import) {
      setFolderPickerOpened(true);
    }
  }, [mode]);

  const resetFormats = React.useCallback(() => {
    setFormats([]);
  }, []);

  // Reset chosen folder/format when switching the datasheet type to avoid stale selections.
  // Important: don't reset on initial mount, otherwise any "initial*" props will be lost.
  const didInitRef = React.useRef(false);
  React.useEffect(() => {
    if (!didInitRef.current) {
      didInitRef.current = true;
      return;
    }
    setSelectedFolder(undefined);
    setSelectedFormat(undefined);
    setSelectedTableFieldId(null);
    setSelectedTableFieldName("");
    setSelectedTableFieldContext(1); // Reset to default IDS context
  }, [selectedFormatType]);

  const openFolderPicker = React.useCallback(() => {    
    if (selectedFormatType === DatasheetFormatType.table && selectedTableFieldId === null) {
      showErrorMessage(trDts("selectTableFirstMessage"));
      return;
    }
    else{
            setFolderPickerOpened(true);
    }
  }, [selectedFormatType, selectedTableFieldId, showErrorMessage]);

  const closeFolderPicker = React.useCallback(() => {
    setFolderPickerOpened(false);
    resetFormats();
    if (mode === DatasheetFormatManagementMode.import) {
      onClose();
    }
  }, [resetFormats, mode, onClose]);

  const handleFormatNameChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFormatName(e.target.value);
  }, []);

  /**
   * Handle table field selection coming from React FieldPicker.
   */
  const handleTableFieldChange = React.useCallback(
    (field: { id: number | string; name: string,context:number }) => {
      const idNumber = typeof field.id === "string" ? parseInt(field.id, 10) : field.id;      
      setSelectedTableFieldId(idNumber);
      setSelectedTableFieldName(field.name);
      setSelectedTableFieldContext(field.context);
    },
    []
  );

  const getContextAndTds = React.useCallback(
    (type: DatasheetFormatType) => {
      if (type === DatasheetFormatType.table) {
        // Table datasheet (TDS) – backend expects tds=true; use context 1 for folder/format APIs.
        return { context: 1, tds: true, tableContext: 1 };
      }
      if (type === DatasheetFormatType.pedigree) {
        // Pedigree datasheet
        return { context: 2, tds: false };
      }
      // Individual datasheet (default)
      return { context: 1, tds: false };
    },
    []
  );

  const submitLoadSelection = React.useCallback(
    (folder: FolderPrimaryListDto, format: { id: number; name: string }) => {
      const { context, tableContext } = getContextAndTds(selectedFormatType);

      onSubmit({
        folder,
        format,
        context,
        tableContext:selectedFormatType===DatasheetFormatType.table ? selectedTableFieldContext : null,
        tableFieldId: selectedFormatType === DatasheetFormatType.table ? selectedTableFieldId : null,
      });

      resetFormats();
      setFolderPickerOpened(false);
    },
    [getContextAndTds, onSubmit, resetFormats, selectedFormatType, selectedTableFieldId]
  );

  const handleSubmit = async () => {
    try {
      if (mode === DatasheetFormatManagementMode.export) {
        if (!selectedFolder) {
          showErrorMessage(trDts("selectFolderMessage"));
          return;
        }
        if (!selectedFormat) {
          showErrorMessage(trDts("selectFormatMessage"));
          return;
        }
        const { context, tds } = getContextAndTds(selectedFormatType);
        onSubmit({
          folder: selectedFolder,
          format: selectedFormat,
          context,
          tds,
        });
        resetFormats();
        setFolderPickerOpened(false);
        return;
      }

      if (selectedFormatType === DatasheetFormatType.table && !selectedFolder && !selectedTableFieldId) {
        showErrorMessage(trDts("selectFolderAndTableFirstMessage"));
        return;
      }
  
      if (!selectedFolder) {
        showErrorMessage(trDts("selectFolderMessage"));
        return;
      }
  
      if (selectedFormatType === DatasheetFormatType.table && !selectedTableFieldId) {
        showErrorMessage(trDts("selectTableFirstMessage"));
        return;
      }
      const doSubmit = async () => {
        /**
         * IMPORT – only folder selection is needed. Hand off the chosen folder.
         */
        if (mode === DatasheetFormatManagementMode.import) {
          if (!selectedFolder) {
            showErrorMessage(trDts("selectFolderMessage"));
            return;
          }

          onSubmit({
            folder: selectedFolder,
          });
          resetFormats();
          setFolderPickerOpened(false);
          return;
        }

        /**
         * LOAD existing format
         */
        if (mode === DatasheetFormatManagementMode.load) {
          if (!selectedFolder) {
            showErrorMessage(trDts("selectFolderMessage"));
            return;
          }

          if (!selectedFormat) {
            showErrorMessage(trDts("selectFormatMessage"));
            return;
          }

          submitLoadSelection(selectedFolder, selectedFormat);
          return;
        }

        /**
         * CREATE new format
         */
        if (!selectedFolder || !formatName.trim().length) {
          showErrorMessage(trDts("Failed to get data"));
          return;
        }

        const { context } = getContextAndTds(selectedFormatType);

        // Decide DS vs TDS based on selected type
        const isTableType = selectedFormatType === DatasheetFormatType.table;
        const type = isTableType ? "tds" : "ds";

        await createDatasheetFormat({
          formatName: formatName.trim(),
          folderId: selectedFolder.id,
          type,
          tableId: isTableType && selectedTableFieldId ? selectedTableFieldId : undefined,
        });

        // After successful creation, get the formatId by querying the folder
        // For table datasheets, use TDS API; otherwise use standard API
        const formats = isTableType && selectedTableFieldId
          ? await getTdsResultFolderFormats({
              folderId: selectedFolder.id,
              context,
              tableId: selectedTableFieldId,
            })
          : await getDatasheetResultFolderFormats({
              folderId: selectedFolder.id,
              context,
            });
        const createdFormat = formats.find(
          (f) => f.name === formatName.trim()
        );

        if (createdFormat) {
          // Open editor in a new tab via executeUI (same pattern as spreadsheets)
          const tableFieldId =
            selectedTableFieldId && selectedFormatType === DatasheetFormatType.table
              ? selectedTableFieldId
              : null;
          const MODULE_NAME = "DATASHEETS";
          const ACTION = "EDIT";

          executeUI(
            MODULE_NAME,
            ACTION,
            formatName.trim(),
            context,
            createdFormat.id,
            tableFieldId,
            selectedTableFieldContext
          )
            .then(() => {
            })
            .catch((err: any) => {
              console.error("Failed to open datasheet editor:", err);
              showErrorMessage(trDts("Failed to get data"));
            });
        } else {
          console.warn(
            `Created format "${formatName.trim()}" not found in folder ${selectedFolder.id}`
          );
          showErrorMessage(trDts("Failed to get data"));
        }

        onSubmit({
          folder: selectedFolder,
          format: selectedFormat,
        });
        resetFormats();
        setFolderPickerOpened(false);
      };

      if (mode === DatasheetFormatManagementMode.new) {
        if (!selectedFolder || !formatName.trim().length) {
          showErrorMessage(trDts("Failed to get data"));
          return;
        }

        // Load formats for the selected folder first
        const { context } = getContextAndTds(selectedFormatType);
        const isTableType = selectedFormatType === DatasheetFormatType.table;
        const existingFormats = isTableType && selectedTableFieldId
          ? await getTdsResultFolderFormats({
              folderId: selectedFolder.id,
              context,
              tableId: selectedTableFieldId,
            })
          : await getDatasheetResultFolderFormats({
              folderId: selectedFolder.id,
              context,
            });

        // Check if format name exists after loading the formats
        const trimmedFormatName = formatName.trim().toLowerCase();
        const existingFormat = existingFormats.find(
          (format) => format.name.trim().toLowerCase() === trimmedFormatName
        );

     

        if (existingFormat) {
          showConfirmMessage({
            cancelText: trDts("Cancel"),
            confirmText: trDts("Overwrite"),
            content: trDts("wouldYouLikeToOverwriteFormatName"),
            onCustomOk: async () => {
              await doSubmit();
            },
          });
          return;
        }
      }

      await doSubmit();
    } catch (err) {
      console.error(err);
      showErrorMessage(
        (err as HttpErrorDto)?.entity || trDts("Failed to get data")
      );
    }
  };

  const getAndSetFormats = React.useCallback(
    async (folderId: number) => {
      const { context } = getContextAndTds(selectedFormatType);
      
      // For table datasheets (TDS), use the TDS-specific API
      if (selectedFormatType === DatasheetFormatType.table && selectedTableFieldId||selectedFormatType===DatasheetFormatType.table && importTableFieldId) {
        const res = await getTdsResultFolderFormats({
          folderId,
          context,
          tableId: selectedTableFieldId||importTableFieldId||0,
        });
        setFormats(res);
      } else {
        // For regular datasheets, use the standard API
        const res = await getDatasheetResultFolderFormats({ folderId, context });
        setFormats(res);
      }
    },
    [getContextAndTds, selectedFormatType, selectedTableFieldId]
  );

  const getAndSetFolders = React.useCallback(async () => {
    let context: number;
    let tds: boolean;

    // For import we want to drive the folders purely from the XML context,
    // not from any UI-selected type (there is no type dropdown in this flow).
    if (mode === DatasheetFormatManagementMode.import && importContext != null) {
      if (importContext === 2) {
        context = 2;
        tds = false;
      } else if (importContext === 8||importTableFieldId && importTableFieldId !== 0) {
        // Table datasheets: use context=1 with tds=true (same convention as getContextAndTds)
        context = 1;
        tds = true;
      } else {
        // Default to individual
        context = 1;
        tds = false;
      }
    } else {
      const mapped = getContextAndTds(selectedFormatType);
      context = mapped.context;
      // Some modes (load) don't care about tds, so default to false if missing.
      tds = (mapped as any).tds ?? false;
    }

    const res = await getDatasheetFolders(context, tds);
    setFolders(res);
  }, [getContextAndTds, selectedFormatType, mode, importContext]);

  const deleteFormat = async (_folderId: number, formatId: number) => {
    const { tds } = getContextAndTds(selectedFormatType);
    await deleteDatasheetFormat(formatId, tds);
  };

  const deleteFolder = async (folderId: number) => {
    const { tds } = getContextAndTds(selectedFormatType);
    await deleteDatasheetFolder({ folderId, tds });
  };

  const createFolder = async (data: { parentid?: number; foldername: string }) => {
    const { context, tds } = getContextAndTds(selectedFormatType);
    await createDatasheetFolder({
      context,
      parentId: data.parentid,
      name: data.foldername,
      tds,
    });
  };

  const handleFormatDelete = React.useCallback(
    async (folderId: number, formatId: number) => {
      await deleteFormat(folderId, formatId);
      await getAndSetFormats(folderId);
    },
    [deleteFormat, getAndSetFolders]
  );

  const handleFolderDelete = React.useCallback(
    async (folderId: number) => {
      await deleteFolder(folderId);
      await getAndSetFolders();
    },
    [deleteFolder, getAndSetFolders]
  );

  const handleFolderCreate = React.useCallback(
    async (data: { parentId?: number; name: string }) => {
      await createFolder({
        parentid: data.parentId,
        foldername: data.name,
      });
      await getAndSetFolders();
    },
    [createFolder, getAndSetFolders]
  );

  const handleFormatRename = (folderId: number, formatId: number, currentName: string) => {
    setRenameState({
      folderId,
      formatId,
      name: currentName,
      opened: true,
    });
  };

  const closeRenameModal = () => {
    setRenameState({
      folderId: null,
      formatId: null,
      name: "",
      opened: false,
    });
  };

  const getExistingFormatId = async (name: string): Promise<number | null> => {
    if (!renameState.folderId || !renameState.formatId) {
      return null;
    }

    const trimmedName = name.trim().toLowerCase();
    const currentNameLower = renameState.name.trim().toLowerCase();
    
    // If the new name matches the current format name (case-insensitive), it's not a conflict
    if (trimmedName === currentNameLower) {
      return null;
    }

    const { context } = getContextAndTds(selectedFormatType);
    const isTableType = selectedFormatType === DatasheetFormatType.table;
    const existingFormats = isTableType && selectedTableFieldId
      ? await getTdsResultFolderFormats({
          folderId: renameState.folderId,
          context,
          tableId: selectedTableFieldId,
        })
      : await getDatasheetResultFolderFormats({
          folderId: renameState.folderId,
          context,
        });

    const existingFormat = existingFormats.find(
      (format) => format.id !== renameState.formatId && format.name.trim().toLowerCase() === trimmedName
    );

    return existingFormat ? existingFormat.id : null;
  };

  const checkFormatNameExists = async (name: string): Promise<boolean> => {
    const existingFormatId = await getExistingFormatId(name);
    return existingFormatId !== null;
  };

  const performRename = async (newName: string) => {
    if (!renameState.formatId || !renameState.folderId) {
      return;
    }

    try {
      const { tds } = getContextAndTds(selectedFormatType);
      const isTableType = selectedFormatType === DatasheetFormatType.table;
      await renameDatasheetFormat({
        formatId: renameState.formatId,
        newFormatName: newName,
        folderId: renameState.folderId,
        tds,
        tableId: isTableType && selectedTableFieldId ? selectedTableFieldId : undefined,
      });

      await getAndSetFormats(renameState.folderId);
      closeRenameModal();
      setSuccessToastMessage(trDts("Datasheet format renamed successfully"));
    } catch (err) {
      console.error(err);
      showErrorMessage(
        (err as HttpErrorDto)?.entity || trDts("Failed to get data")
      );
    }
  };

  const handleRenameConfirm = async (newName: string) => {
    const nameExists = await checkFormatNameExists(newName);
    if (nameExists) {
      if (renameOverwritePromptVisibleRef.current) {
        return;
      }
      renameOverwritePromptVisibleRef.current = true;
      showConfirmMessage({
        header: trDts("Confirmation Message"),
        cancelText: trDts("Cancel"),
        confirmText: trDts("Overwrite"),
        content: trDts("This name has already been used, would you like to overwrite the current format being used by this name?"),
        onCustomOk: async () => {
          try {
            // Get the existing format ID that needs to be deleted
            const existingFormatId = await getExistingFormatId(newName);
            
            if (existingFormatId) {
              // Delete the existing format first
              const { tds } = getContextAndTds(selectedFormatType);
              await deleteDatasheetFormat(existingFormatId, tds);
            }
            
            // Then rename the current format
            await performRename(newName);
          } catch (err) {
            console.error(err);
            showErrorMessage(
              (err as HttpErrorDto)?.entity || trDts("Failed to get data")
            );
          }
        },
      });
      return;
    }

    await performRename(newName);
  };

  const defaultFormatTypes = React.useMemo(
    () => [
      { value: DatasheetFormatType.individual, label: trDts("Individual") },
      { value: DatasheetFormatType.pedigree, label: trDts("Pedigree") },
      { value: DatasheetFormatType.table, label: trDts("Table") },
    ],
    []
  );

  const config: Record<DatasheetFormatManagementMode, DatasheetFormatManagementConfig> = {
    [DatasheetFormatManagementMode.export]: {
      title: trDts("Export Datasheet Format"),
      submitBtnText: trDts("Export"),
      isAllowedToSubmit: (params: {
        folderId?: FolderPrimaryListDto["id"];
        formatId?: DatasheetFormatManagementFormat["id"];
        newFormatName?: string;
      }) => Boolean(params.formatId),
      formatManagement: {
        title: trDts("Export Format"),
        isAllowedSubmit: ({ formatId }: { formatId?: number }) => Boolean(formatId),
        submitBtnProps: {
          text: trDts("OK"),
        },
      },
    },
    [DatasheetFormatManagementMode.load]: {
      title: trDts("Load Datasheet Format"),
      submitBtnText: trDts("Load"),
      isAllowedToSubmit: (params: {
        folderId?: FolderPrimaryListDto["id"];
        formatId?: DatasheetFormatManagementFormat["id"];
        newFormatName?: string;
      }) => Boolean(params.formatId),
      formatManagement: {
        title: trDts("Load Format"),
        isAllowedSubmit: ({ formatId }: { formatId?: number }) => Boolean(formatId),
        submitBtnProps: {
          text: trDts("Load"),
        },
        deleteFormatBtnProps: {
          text: trDts("Delete Format"),
        },
        handleFormatDelete,
        handleFolderDelete,
        handleFolderCreate,
        handleFormatRename,
      },
    },
    [DatasheetFormatManagementMode.new]: {
      title: trDts("Add New Datasheet"),
      submitBtnText: trDts("Create"),
      isAllowedToSubmit: (params: {
        folderId?: FolderPrimaryListDto["id"];
        formatId?: DatasheetFormatManagementFormat["id"];
        newFormatName?: string;
      }) => {
        const hasFolder = Boolean(params.folderId);
        const hasName = Boolean(params.newFormatName?.trim().length);
        const needsTableField =
          selectedFormatType === DatasheetFormatType.table ? Boolean(selectedTableFieldId) : true;
        return hasFolder && hasName && needsTableField;
      },
      showFormatName: true,
      formatManagement: {
        title: trDts("New Format"),
        isAllowedSubmit: ({ folderId }: { folderId?: number }) => Boolean(folderId),
        submitBtnProps: {
          text: trDts("OK"),
        },
        deleteFormatBtnProps: {
          text: trDts("Delete Format"),
        },
        handleFormatDelete,
        handleFolderDelete,
        handleFolderCreate,
        handleFormatRename,
      },
    },
    [DatasheetFormatManagementMode.import]: {
      title: trDts("Import Datasheet Format"),
      submitBtnText: trDts("OK"),
      isAllowedToSubmit: ({ folderId }) => Boolean(folderId),
      formatManagement: {
        title: trDts("Import Datasheet Format"),
        isAllowedSubmit: ({ folderId }: { folderId?: number; formatId?: number }) => Boolean(folderId),
        submitBtnProps: { text: trDts("OK") },
      },
    },
  };

  const modeConfig = config[mode];

  const isAllowedToSubmitFormatOrFolderLoader = React.useCallback((params: { folderId: number; formatId?: number }) => {
    return Boolean(params.folderId);
  }, []);

  const handleFolderSelect = React.useCallback(
    async (
      format: DatasheetFormatManagementSubmitExportParams["format"],
      folder: DatasheetFormatManagementSubmitExportParams["folder"]
    ) => {
      setSelectedFolder(folder);
      setSelectedFormat(format);

      if (mode === DatasheetFormatManagementMode.load) {
        if (!folder) {
          showErrorMessage(trDts("selectFolderMessage"));
          return;
        }
        if (!format) {
          showErrorMessage(trDts("selectFormatMessage"));
          return;
        }
        submitLoadSelection(folder, format);
        return;
      }

      if (mode === DatasheetFormatManagementMode.import) {
        if (!importFormatName) {
          onSubmit({ folder });
          return;
        }

        try {
          let context = 1;
          if (importContext === 2) {
            context = 2;
          } else if (importContext === 8) {
            context = 1;
          }

          // For TDS imports (context 8), use TDS API.
          // Prefer the UI-selected table field (selectedTableFieldId) when present,
          // otherwise fall back to the table field coming from the import XML (importTableFieldId).
          const effectiveTableFieldId = selectedTableFieldId || importTableFieldId || 0;
          const shouldUseTdsFormats =
            importContext === 8 && effectiveTableFieldId !== 0;

          const existingFormats = shouldUseTdsFormats
            ? await getTdsResultFolderFormats({
                folderId: folder.id,
                context,
                tableId: effectiveTableFieldId,
              })
            : await getDatasheetResultFolderFormats({
                folderId: folder.id,
                context,
              });
          const trimmedName = importFormatName.trim().toLowerCase();
          const duplicate = existingFormats.find(
            (x) => x.name.trim().toLowerCase() === trimmedName
          );

          if (duplicate) {
            showConfirmMessage({
              header: trDts("Confirmation Message"),
              cancelText: trDts("No"),
              confirmText: trDts("Yes"),
              content: React.createElement(
                QuestionMessage,
                null,
                trDts("confirmOverwriteDsFormat")
              ),
              onCustomOk: () => {
                onSubmit({ folder });
              },
            });
          } else {
            onSubmit({ folder });
          }
        } catch (err) {
          console.error(err);
          showErrorMessage(
            (err as HttpErrorDto)?.entity || trDts("Failed to get data")
          );
        }
        return;
      }

      closeFolderPicker();
    },
    [
      closeFolderPicker,
      mode,
      submitLoadSelection,
      onSubmit,
      importContext,
      importTableFieldId,
      importFormatName,
      showConfirmMessage,
      showErrorMessage,
      selectedTableFieldId,
    ]
  );

  return {
    defaultFormatTypes,
    selectedFormatType,
    formatName,
    folders,
    formats,
    selectedFolder,
    selectedFormat,
    selectedTableFieldId,
    selectedTableFieldName,
    selectedTableFieldContext,
    folderPickerOpened,
    getAndSetFolders,
    handleFolderSelect,
    isAllowedToSubmitFormatOrFolderLoader,
    getAndSetFormats,
    handleSubmit,
    openFolderPicker,
    closeFolderPicker,
    handleFormatNameChange,
    handleTableFieldChange,
    setSelectedFormatType,
    resetFormats,
    modeConfig,
    closeMessage,
    showConfirmMessage,
    messageProps,
    showErrorMessage,
    renameModalOpened: renameState.opened,
    renameFormatName: renameState.name,
    closeRenameModal,
    handleRenameConfirm,
    checkFormatNameExists,
    successToastMessage,
    clearSuccessToastMessage,
  };
};
